import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Banner, Button, Select } from '@meshble/molo';
import type { components } from '../api/schema';
import { api, must } from '../lib/api';
import { Anello, Brandmark, Logo } from '../brand';
import { Login, isUnauthenticated, useMe } from '../session';
import { t } from '../lib/lingua';

/**
 * Il consenso di un assistente (blocco 27, docs/research/08-mcp.md §5.2).
 *
 * claude.ai o Claude Desktop mandano qui il browser con la richiesta OAuth
 * nell'indirizzo. La pagina chiede all'hub di validarla — client, redirect,
 * organizzazione, ruolo — e mostra quello che la specifica vuole che si veda:
 * chi chiede, come chi, su cosa, e **dove andrà il codice**, perché è lì che
 * finisce l'accesso. «Consenti» e «Rifiuta» passano dall'hub, che risponde con
 * l'indirizzo a cui tornare.
 *
 * Sta fuori dallo shell come l'invito: chi arriva da claude.ai può non essere
 * ancora entrato, e il login lo riporta qui con tutti i parametri.
 */

type Richiesta = components['schemas']['Richiesta'];

export type CercaCollega = {
  response_type: string;
  client_id: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  state: string;
  scope: string;
  resource: string;
};

export function validaCercaCollega(s: Record<string, unknown>): CercaCollega {
  const v = (k: string) => (typeof s[k] === 'string' ? (s[k] as string) : '');
  return {
    response_type: v('response_type'), client_id: v('client_id'), redirect_uri: v('redirect_uri'),
    code_challenge: v('code_challenge'), code_challenge_method: v('code_challenge_method'),
    state: v('state'), scope: v('scope'), resource: v('resource'),
  };
}

function nomeProfilo(p: string): string {
  const nomi: Record<string, string> = {
    lettura: t('Lettura — guarda, non cambia niente'),
    amministrazione: t('Amministrazione — anche le azioni accese nella pagina «Assistenti»'),
  };
  return nomi[p] ?? p;
}

export function CollegaPage() {
  const s = useSearch({ from: '/collega' }) as CercaCollega;
  const me = useMe();
  const richiesta: Richiesta = {
    responseType: s.response_type, clientId: s.client_id, redirectUri: s.redirect_uri,
    codeChallenge: s.code_challenge, codeChallengeMethod: s.code_challenge_method,
    state: s.state, scope: s.scope, resource: s.resource,
  };
  const entrato = me.isSuccess;

  const ante = useQuery({
    queryKey: ['collega', s],
    queryFn: () => must(api.POST('/api/v1/assistants/authorize/preview', { body: richiesta })),
    enabled: entrato && !!s.client_id,
    // Una richiesta sbagliata non migliora riprovando.
    retry: false,
  });
  const [profilo, setProfilo] = useState('');

  const decidi = useMutation({
    mutationFn: (allow: boolean) =>
      must(api.POST('/api/v1/assistants/authorize', {
        body: { ...richiesta, profile: (profilo || ante.data?.proposed || 'lettura') as 'lettura' | 'amministrazione', allow },
      })),
    // Si torna al client con il codice (o con il rifiuto): è un altro sito,
    // quindi una navigazione vera e non del router.
    onSuccess: (r) => window.location.assign(r.redirect),
  });

  if (!s.client_id) {
    return (
      <Cornice>
        <Banner tone="error" title={t('Richiesta incompleta')}>
          {t('Questa pagina si apre dall’assistente che chiede di collegarsi, con la sua richiesta nell’indirizzo.')}
        </Banner>
      </Cornice>
    );
  }
  if (me.isPending || (entrato && ante.isPending)) {
    return (
      <main className="md-login">
        <div className="md-login__card"><Logo size={56} anim="costruzione" label={t('Carico la richiesta')} /></div>
      </main>
    );
  }
  if (isUnauthenticated(me.error)) {
    return (
      <Login sopra={
        <Banner title={t('Un assistente chiede di collegarsi a Molo')}>
          {t('Entra per vedere chi è e decidere cosa può fare: il collegamento avrà i tuoi diritti, o meno.')}
        </Banner>
      } />
    );
  }
  if (ante.error) {
    return (
      <Cornice>
        <Banner tone="error" title={t('Non posso collegare questo assistente')}>{ante.error.message}</Banner>
        <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Non ti rimando all’assistente: con una richiesta che non torna, il posto giusto per l’errore è qui.')}
        </p>
      </Cornice>
    );
  }
  const a = ante.data!;
  const scelto = profilo || a.proposed;

  return (
    <Cornice>
      <h1 className="md-title-large" style={{ margin: 0 }}>{t('«{client}» chiede di collegarsi a Molo', { client: a.client })}</h1>
      <dl className="md-dati">
        <div className="md-dato"><dt>{t('Come')}</dt><dd>{a.persona}</dd></div>
        <div className="md-dato"><dt>{t('Organizzazione')}</dt><dd>{a.org}</dd></div>
        <div className="md-dato"><dt>{t('Il codice andrà a')}</dt><dd className="md-mono">{a.redirectHost}</dd></div>
      </dl>
      <div className="md-login__dev">
        <Select
          label={t('Cosa può fare')}
          value={scelto}
          options={(a.profiles ?? []).map((p) => ({ value: p, label: nomeProfilo(p) }))}
          onChange={(e) => setProfilo(e.target.value)}
        />
      </div>
      <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Con i tuoi diritti, mai di più, e solo con i tool accesi per l’organizzazione. Il collegamento resta finché l’assistente lo usa (si rinnova ogni trenta giorni) e si revoca dalla pagina «Assistenti». Ogni cosa che fa finisce nel registro con il tuo nome e quello dell’assistente.')}
      </p>
      {decidi.error && <Banner tone="error" title={t('Non è andata')}>{(decidi.error as Error).message}</Banner>}
      <div className="md-form__actions">
        <Button variant="text" disabled={decidi.isPending} onClick={() => decidi.mutate(false)}>{t('Rifiuta')}</Button>
        <Button variant="filled" icon={decidi.isPending ? <Anello /> : undefined} disabled={decidi.isPending}
          onClick={() => decidi.mutate(true)}>
          {t('Consenti')}
        </Button>
      </div>
    </Cornice>
  );
}

function Cornice({ children }: { children: React.ReactNode }) {
  return (
    <main className="md-login">
      <div className="md-login__card md-login__card--largo">
        <Brandmark />
        {children}
      </div>
    </main>
  );
}
